<template>
    <div class="position-relative">
        <div v-if="hasBookings" class="row g-3">
            <div
                v-for="booking in bookings"
                :key="booking.id"
                class="col-12 col-sm-6 col-md-4 col-lg-3"
            >
                <div class="guest-card h-100">
                    <div class="status-accent"></div>
                    <div class="guest-card-body">
                        <div class="guest-head">
                            <div class="avatar">{{ initial(booking.guest_name) }}</div>
                            <div class="guest-head-text">
                                <div class="guest-name" :title="booking.guest_name">{{ booking.guest_name }}</div>
                                <div class="reservation-no">{{ booking.reservation_no }}</div>
                            </div>
                            <span class="status-badge">{{ statusLabel(booking.registration_status) }}</span>
                        </div>
                        <div class="rooms-line">
                            <i class="fa fa-bed me-2"></i>
                            <span class="rooms-text">{{ roomLabel(booking.rooms) }}</span>
                            <span v-if="roomCount(booking.rooms) > 1" class="room-count">{{ roomCount(booking.rooms) }} rooms</span>
                        </div>
                        <div class="dates-line">
                            <div class="date-item">
                                <i class="fa fa-sign-in-alt me-1"></i>{{ dateLabel(booking.check_in) }}
                            </div>
                            <div class="date-item">
                                <i class="fa fa-sign-out-alt me-1"></i>{{ dateLabel(booking.discharge_date) }}
                            </div>
                        </div>
                        <div class="guest-card-footer">
                            <span class="paid-amount">Paid: {{ booking.paid }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!hasBookings && !loading" class="empty-state">
            No pre-booked room found
        </div>

        <div class="mt-3">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { fetchPreBookedRoomList } = handleHotel();

const offset = ref(20);
const loading = ref(false);
const bookings = ref([]);

const hasBookings = computed(() => bookings.value.length > 0);

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getPreBookedList = async () => {
    loading.value = true;
    bookings.value = [];
    await fetchPreBookedRoomList(getQuery()).then(res => {
        if (!res.status) {
            $store.commit('resetPagination');
            return;
        }
        bookings.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    await getPreBookedList();
};

const roomLabel = (rooms) => {
    if (!rooms) return '—';
    return rooms;
};

const roomCount = (rooms) => {
    if (!rooms) return 0;
    return rooms.split(',').length;
};

const initial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
};

const dateLabel = (date) => {
    if (!date) return '—';
    return date;
};

const statusLabel = (status) => {
    if (!status) return '—';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

onMounted(() => {
    getPreBookedList();
});
</script>

<style scoped>
.guest-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #eceef1;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
}
.guest-card:hover {
    box-shadow: 0 10px 28px rgba(16, 24, 40, 0.14);
    transform: translateY(-3px);
    border-color: #dfe3e8;
}
.status-accent {
    height: 5px;
    background: linear-gradient(90deg, #6366f1, #8b8cf7);
}
.guest-card-body {
    padding: 16px 18px 18px;
}
.guest-head {
    display: flex;
    align-items: center;
    gap: 10px;
}
.avatar {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: #3730a3;
    background: #e8e8fd;
}
.guest-head-text {
    min-width: 0;
    flex: 1;
}
.guest-name {
    font-size: 0.95rem;
    font-weight: 700;
    color: #101828;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.reservation-no {
    margin-top: 2px;
    font-size: 0.72rem;
    letter-spacing: 0.03em;
    color: #98a2b3;
}
.status-badge {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #3730a3;
    background: #e8e8fd;
    height: fit-content;
}
.rooms-line {
    display: flex;
    align-items: center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #eceef1;
    font-size: 0.86rem;
    color: #475467;
}
.rooms-line .fa-bed {
    color: #98a2b3;
}
.rooms-text {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.room-count {
    flex-shrink: 0;
    margin-left: auto;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #475467;
    background: #f2f4f7;
}
.dates-line {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 0.78rem;
    color: #667085;
}
.date-item .fa-sign-in-alt,
.date-item .fa-sign-out-alt {
    color: #98a2b3;
}
.guest-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
}
.paid-amount {
    font-size: 0.82rem;
    font-weight: 600;
    color: #1a7a49;
}
.empty-state {
    text-align: center;
    color: #98a2b3;
    padding: 60px 0;
    font-size: 0.95rem;
}
</style>
